import React, { useState, useEffect } from 'react';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import {
    Modal,
    ConfirmModal,
    Button,
    FormGroup,
    FormLabel,
    FormControl
} from '../components/adminModals';
import { categoryService, Category, CreateCategoryPayload, UpdateCategoryPayload } from '../services/adminAuth';
import { Plus, Pencil, Power, PowerOff } from 'lucide-react';
import Swal from 'sweetalert2';

function ManageCategory() {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        code: ''
    });

    const [searchQuery, setSearchQuery] = useState('');

    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await categoryService.getAllCategories();
            if (response.isSuccess) {
                setCategories(response.data);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to fetch thematic areas'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while fetching thematic areas'
            });
            } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!formData.name || !formData.code) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill in all fields'
            });
            return;
        }

        if (formData.code.length > 2) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Code',
                text: 'Thematic area code cannot be more than 2 characters'
            });
            return;
        }

        setSubmitting(true);
        try {
            const payload: CreateCategoryPayload = {
                name: formData.name,
                code: formData.code,
                id: 0
            };

            const response = await categoryService.createCategory(payload);
            if (response.isSuccess) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Thematic area created successfully',
                    timer: 1500,
                    showConfirmButton: false
                });
                setIsAddModalOpen(false);
                setFormData({ name: '', code: '' });
                await fetchCategories();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to create thematic area'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while creating the thematic area'
            });
            } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async () => {
        if (!selectedCategory || !formData.name || !formData.code) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill in all fields'
            });
            return;
        }

        if (formData.code.length > 2) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Code',
                text: 'Thematic area code cannot be more than 2 characters'
            });
            return;
        }

        setSubmitting(true);
        try {
            const payload: UpdateCategoryPayload = {
                id: selectedCategory.id,
                name: formData.name,
                code: formData.code
            };

            const response = await categoryService.updateCategory(payload);
            if (response.isSuccess) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Thematic area updated successfully',
                    timer: 1500,
                    showConfirmButton: false
                });
                setIsEditModalOpen(false);
                setSelectedCategory(null);
                setFormData({ name: '', code: '' });
                await fetchCategories();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to update thematic area'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while updating the thematic area'
            });
            } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async () => {
        if (!selectedCategory) return;

        const newStatus = !selectedCategory.active;
        const actionLabel = newStatus ? 'activated' : 'deactivated';

        setSubmitting(true);
        try {
            const response = await categoryService.toggleCategoryStatus(selectedCategory.id, newStatus);
            if (response.isSuccess) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `Thematic area ${actionLabel} successfully`,
                    timer: 1500,
                    showConfirmButton: false
                });
                setIsToggleModalOpen(false);
                setSelectedCategory(null);
                await fetchCategories();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} thematic area`
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `An error occurred while ${newStatus ? 'activating' : 'deactivating'} the thematic area`
            });
            } finally {
            setSubmitting(false);
        }
    };

    const openEdit = (cat: Category) => {
        setSelectedCategory(cat);
        setFormData({ name: cat.name, code: cat.code });
        setIsEditModalOpen(true);
    };

    const openToggle = (cat: Category) => {
        setSelectedCategory(cat);
        setIsToggleModalOpen(true);
    };

    const columns: TableColumn<Category>[] = [
        {
            key: 'name',
            label: 'Name',
            className: 'font-medium'
        },
        {
            key: 'code',
            label: 'Code'
        },
        {
            key: 'active',
            label: 'Status',
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${value
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                    {value ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            key: 'createdDate',
            label: 'Created Date',
            render: (value) => new Date(value).toLocaleDateString()
        }
    ];

    const actions: TableAction<Category>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            title: 'More options',
            dropdownItems: [
                {
                    icon: <Pencil size={16} />,
                    label: 'Edit',
                    onClick: openEdit,
                    className: 'text-blue-600'
                },
                {
                    icon: (row) => row.active ? <PowerOff size={16} /> : <Power size={16} />,
                    label: (row) => row.active ? 'Deactivate' : 'Activate',
                    onClick: openToggle,
                    className: (row) => row.active ? 'text-red-600' : 'text-green-600'
                }
            ]
        }
    ];

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    {/* Header */}
                    <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
    
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                Thematic Area Management
                            </h1>
                            <p className="text-gray-500 text-sm sm:text-base">
                                Manage manuscript thematic areas and codes.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                            <Button
                                variant="success"
                                onClick={() => {
                                    setIsAddModalOpen(true);
                                    setFormData({ name: '', code: '' });
                                }}
                                className="w-full sm:w-auto flex items-center justify-center gap-2"
                            >
                                <Plus size={16} />
                                Add New Thematic Area
                            </Button>
                        </div>

                    </div>

                    <ReusableTable
                        title="Thematic Areas"
                        subTitle="List of all defined thematic areas"
                        columns={columns}
                        data={filteredCategories}
                        actions={actions}
                        loading={loading}
                        showSearch={true}
                        onSearch={setSearchQuery}
                        searchPlaceholder="Search thematic areas..."
                        emptyMessage="No thematic areas found. Create your first thematic area!"
                    />

                    <Modal
                        isOpen={isAddModalOpen}
                        onClose={() => {
                            setIsAddModalOpen(false);
                            setFormData({ name: '', code: '' });
                        }}
                        title="Add New Thematic Area"
                        description="Create a new thematic area for manuscripts"
                    >
                        <div>
                            <FormGroup>
                                <FormLabel required>Name</FormLabel>
                                <FormControl
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g. Science"
                                    disabled={submitting}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel required>Code</FormLabel>
                                <FormControl
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                    placeholder="e.g. SC"
                                    maxLength={2}
                                    disabled={submitting}
                                    required
                                />
                            </FormGroup>

                            <div className="flex gap-3 mt-6">
                                <Button
                                    variant="white"
                                    onClick={() => setIsAddModalOpen(false)}
                                    className="flex-1"
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={handleAdd}
                                    className="flex-1"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Adding...' : 'Add Thematic Area'}
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {/* Edit Modal */}
                    <Modal
                        isOpen={isEditModalOpen}
                        onClose={() => {
                            setIsEditModalOpen(false);
                            setSelectedCategory(null);
                            setFormData({ name: '', code: '' });
                        }}
                        title="Edit Thematic Area"
                        description="Update thematic area details"
                    >
                        <div>
                            <FormGroup>
                                <FormLabel required>Name</FormLabel>
                                <FormControl
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    disabled={submitting}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel required>Code</FormLabel>
                                <FormControl
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                    maxLength={2}
                                    disabled={submitting}
                                    required
                                />
                            </FormGroup>

                            <div className="flex gap-3 mt-6">
                                <Button
                                    variant="white"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="flex-1"
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={handleUpdate}
                                    className="flex-1"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Updating...' : 'Update Thematic Area'}
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {/* Activate / Deactivate Confirmation Modal */}
                    <ConfirmModal
                        isOpen={isToggleModalOpen}
                        onClose={() => {
                            setIsToggleModalOpen(false);
                            setSelectedCategory(null);
                        }}
                        onConfirm={handleToggleStatus}
                        title={selectedCategory?.active ? 'Deactivate Thematic Area' : 'Activate Thematic Area'}
                        message={`Are you sure you want to ${selectedCategory?.active ? 'deactivate' : 'activate'} the thematic area "${selectedCategory?.name}"?`}
                        confirmText={selectedCategory?.active ? 'Deactivate' : 'Activate'}
                        cancelText="Cancel"
                        variant={selectedCategory?.active ? 'danger' : 'info'}
                        isSubmitting={submitting}
                    />
                </div>
            </div>
        </div>
    );
}

export default ManageCategory;
